import { subscribeReferralConsent } from '@/services/consentInvalidation.js';
// src/pages/referrals/PatientReferralDetails.jsx //
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";

import LayoutShell from "@/components/ui/LayoutShell.jsx";
import Badge from "@/components/ui/Badge.jsx";
import BasicDialog from "@/components/ui/BasicDialog.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import ReferralSummaryCard from "@/components/referrals/ReferralSummaryCard.jsx";
import { NotificationActionButton } from "@/features/notifications/NotificationButtons.jsx";
import { loadDoctorBillingBranding } from "@/services/doctorBillingBrandingService.js";
import { ensureReferralPdfStored, generateReferralPdfBlob } from "@/services/referralDocumentService.js";
import { getSupabaseAnonKey, getSupabaseBaseUrl } from "@/services/supabaseClient";
import { sendReferralUpdateEmail } from "@/services/communicationEmailService.js";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { resolveValidatedSession } from "@/lib/authSession.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { resolvePatientDisplayName } from "@/utils/patientDisplayName.js";
import { decideReferralPackage } from "@/services/referralPackageService.js";
import messagingService from "@/services/messagingService.js";

const REFERRAL_SHARE_LINK_TIMEOUT_MS = 2500;
const ICON_PRINT = "/icons/appointments/e-prescription/print-filled.svg";
const ICON_DOWNLOAD = "/icons/appointments/e-prescription/download.svg";

function ActionIconButton({ icon, label, onClick, disabled = false }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-10 w-10 items-center justify-center rounded-[12px] opacity-90 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(2,6,23,0.18)] disabled:cursor-not-allowed disabled:opacity-50"
    >
      <img src={icon} alt="" className="h-5 w-5" draggable="false" />
    </button>
  );
}

function safeText(value, fallback = "") {
  const text = String(value || "").trim();
  return text || fallback;
}

function buildDisplayName(row = null, fallback = "Doctor") {
  const fullName = safeText(row?.full_name);
  if (fullName) return fullName;

  const parts = [safeText(row?.first_name), safeText(row?.last_name)].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");

  return fallback;
}

function buildDoctorReferralName(row = null, fallback = "Doctor") {
  const doctorName = resolvePatientFacingDoctorName(row);
  if (doctorName && doctorName !== "Doctor") return doctorName;
  return fallback;
}

function buildFacilityAddress(row = null) {
  return [
    safeText(row?.facility_address),
    safeText(row?.facility_city),
    safeText(row?.facility_country),
  ]
    .filter(Boolean)
    .join(", ");
}

function buildFacilityPostalAddressLines(row = null) {
  const localityLine = [safeText(row?.facility_city), safeText(row?.postal_code)]
    .filter(Boolean)
    .join(", ");

  return [
    safeText(row?.facility_address),
    localityLine,
    safeText(row?.facility_country),
  ].filter(Boolean);
}

function formatDoctorCtaLabel(name = "") {
  const doctorName = resolvePatientFacingDoctorName(name);
  if (!doctorName || doctorName === "Doctor") return "Book with specialist";
  return `Book with ${doctorName}`;
}

function buildSpecialistSearchHref({ specialty = "", doctorName = "" }) {
  const params = new URLSearchParams();
  if (safeText(specialty)) params.set("specialty", safeText(specialty));
  if (safeText(doctorName)) params.set("q", safeText(doctorName));
  const query = params.toString();
  return query ? `/appointments/doctors?${query}` : "/appointments/doctors";
}

function buildReferralPdfCopy(t) {
  return {
    headingUrgent: t("referrals.pdf.headingUrgent", "Urgent referral"),
    headingDefault: t("referrals.pdf.headingDefault", "Referral"),
    subtitle: t("referrals.pdf.subtitle", "Your referral document issued by your doctor."),
    date: t("referrals.summary.date", "Date"),
    to: t("referrals.summary.to", "To"),
    from: t("referrals.summary.from", "From"),
    patient: t("referrals.summary.patient", "Patient"),
    reason: t("referrals.summary.reason", "Reason for referral"),
    clinicalHistory: t(
      "referrals.summary.clinicalHistory",
      "Clinical history & findings"
    ),
    currentMedications: t("referrals.pdf.currentMedications", "Current medications"),
    specificRequest: t("referrals.summary.specificRequest", "Specific request"),
    urgency: t("referrals.summary.urgency", "Urgency"),
    signature: t("referrals.summary.signature", "Physician signature"),
    signatureIssuedBy: t(
      "referrals.summary.signatureIssuedBy",
      "Issued by {doctorLabel}."
    ),
    signatureOnFile: t("referrals.summary.signatureOnFile", "Signature on file."),
    clinicalResponsibility: t("referrals.summary.clinicalResponsibility", "Powered by AfriMed™. The referring clinician remains responsible for clinical decisions."),
  };
}

function buildReferralMailto({ subject = "", body = "", recipientEmail = "" }) {
  return `mailto:${safeText(recipientEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function buildReferralEmailContent({
  referral,
  documentContext,
  patientName = "",
  patientEmail = "",
  recipientEmail = "",
  shortUrl = "",
  t,
}) {
  const hasShortUrl = Boolean(safeText(shortUrl));
  const specialty = safeText(referral?.to_specialty_label, "specialist care");
  const doctorName = safeText(documentContext?.doctorName, "my doctor");
  const facilityName = safeText(documentContext?.facilityName);
  const senderName = safeText(patientName, documentContext?.patientName || "Patient");
  const senderEmail = safeText(patientEmail);
  const recipient = safeText(recipientEmail);
  const specificRequest = safeText(
    referral?.specific_request,
    t("referrals.email.notSpecified", "Not specified")
  );
  const urgency = safeText(referral?.urgency, "routine");

  const subject = `${
    urgency.toLowerCase() === "urgent"
      ? t("referrals.email.subject.urgentPrefix", "Urgent ")
      : ""
  }${t("referrals.email.subject.referralFor", "Referral for {specialty}", {
    specialty,
  })}`;

  const body = [
    t("referrals.email.greeting", "Hello,"),
    "",
    t("referrals.email.body.seekAppointment", "I am seeking a {specialty} appointment for myself.", {
      specialty,
    }),
    "",
    t(
      "referrals.email.body.issuedBy",
      "My referral was issued by {doctorName}{facilitySuffix}.",
      {
        doctorName,
        facilitySuffix: facilityName ? ` at ${facilityName}` : "",
      }
    ),
    "",
    t("referrals.email.body.documentLabel", "Secure referral document:"),
    "",
    hasShortUrl
      ? shortUrl
      : t(
          "referrals.email.body.documentFallback",
          "Please access your referral via AfriMed or request it from me directly."
        ),
    "",
    `${t("referrals.summary.to", "To")}: ${specialty}`,
    `${t("referrals.summary.specificRequest", "Specific request")}: ${specificRequest}`,
    `${t("referrals.summary.urgency", "Urgency")}: ${urgency}`,
    "",
    t("referrals.email.body.assist", "Please let me know if your practice can assist."),
    ...(hasShortUrl
      ? [
          "",
          t("referrals.email.body.expiry", "This secure referral link expires in 7 days."),
        ]
      : []),
    "",
    t("referrals.email.signoff", "Kind regards,"),
    senderName,
    senderEmail || "",
  ].join("\n");

  return {
    recipientEmail: recipient,
    subject,
    body,
  };
}

function parseIsoDateToEpochMs(value) {
  const parsed = Date.parse(safeText(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

async function requestReferralShareToken({
  referralId = "",
  accessToken = "",
  timeoutMs = REFERRAL_SHARE_LINK_TIMEOUT_MS,
}) {
  const baseUrl = getSupabaseBaseUrl();
  const anonKey = getSupabaseAnonKey();
  const cleanReferralId = safeText(referralId);
  const cleanAccessToken = safeText(accessToken);

  if (!baseUrl || !anonKey || !cleanReferralId || !cleanAccessToken) {
    throw new Error("Referral short link prerequisites are missing.");
  }

  const controller = new AbortController();
  const timeoutHandle = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${baseUrl}/functions/v1/create-referral-share-link`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey,
        Authorization: `Bearer ${cleanAccessToken}`,
      },
      body: JSON.stringify({ referral_id: cleanReferralId }),
      signal: controller.signal,
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(
        safeText(payload?.message) ||
          safeText(payload?.error) ||
          `Referral short link request failed (${response.status}).`
      );
      error.status = response.status;
      throw error;
    }

    const token = safeText(payload?.token);
    if (!token) {
      throw new Error("Referral short link response missing token.");
    }

    return {
      token,
      expiresAt: parseIsoDateToEpochMs(payload?.expires_at),
    };
  } catch (error) {
    if (error?.name === "AbortError") {
      const timeoutError = new Error("Referral short link request timed out.");
      timeoutError.code = "timeout";
      throw timeoutError;
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutHandle);
  }
}

export default function PatientReferralDetails() {
  const { referralId } = useParams();
  const nav = useNavigate();
  const { loading: authLoading, user, profile } = useAuth();
  const { t } = useLanguage();

  const [referral, setReferral] = React.useState(null);
  const [loadState, setLoadState] = React.useState("loading");
  const [documentContext, setDocumentContext] = React.useState(null);
  const [acceptedRouting, setAcceptedRouting] = React.useState(null);
  const [referralPackage, setReferralPackage] = React.useState(null);
  const [packageDecisionBusy, setPackageDecisionBusy] = React.useState(false);
  const [packageDecisionError, setPackageDecisionError] = React.useState("");
  const [alwaysAllowConfirmOpen, setAlwaysAllowConfirmOpen] = React.useState(false);
  const [downloadBusy, setDownloadBusy] = React.useState(false);
  const [messageReferrerBusy, setMessageReferrerBusy] = React.useState(false);
  const [emailDraftBusy, setEmailDraftBusy] = React.useState(false);
  const [emailSendState, setEmailSendState] = React.useState({
    error: "",
    success: "",
  });
  const pdfPreparationRef = React.useRef(null);
  const pdfPreparedRef = React.useRef(false);
  const sessionAccessTokenRef = React.useRef("");
  const shareLinkCacheRef = React.useRef({ referralId: "", token: "", expiresAt: 0 });
  const shareLinkRequestRef = React.useRef(null);
  const printRef = React.useRef(null);

  async function messageReferringClinician() {
    const referringDoctorUserId = safeText(referral?.from_doctor_user_id);
    if (!referringDoctorUserId) return;
    setMessageReferrerBusy(true);
    try {
      const conversation = await messagingService.getOrCreateDirectConversation({
        otherUserId: referringDoctorUserId,
        title: documentContext?.doctorName || "Referring clinician",
        type: "doctor_doctor",
      });
      nav(`/messages/${conversation.id}`, {
        state: { participantUserId: referringDoctorUserId, conversationType: "doctor_doctor" },
      });
    } catch (error) {
      console.error("[ReferralDetails] referring clinician conversation failed", error);
      setPackageDecisionError("The clinician conversation could not be opened. Please try again.");
    } finally {
      setMessageReferrerBusy(false);
    }
  }

  function getCachedShareToken(currentReferralId = "") {
    const cache = shareLinkCacheRef.current;
    if (
      safeText(cache?.referralId) !== safeText(currentReferralId) ||
      !safeText(cache?.token) ||
      Number(cache?.expiresAt || 0) <= Date.now() + 60_000
    ) {
      return "";
    }
    return safeText(cache.token);
  }

  async function ensureReferralShareLink(currentReferralId = "", accessToken = "") {
    const cleanReferralId = safeText(currentReferralId);
    const cleanAccessToken = safeText(accessToken);
    const cachedToken = getCachedShareToken(cleanReferralId);
    if (cachedToken) return cachedToken;
    if (!cleanReferralId || !cleanAccessToken) return "";
    if (shareLinkRequestRef.current) {
      const pendingToken = await shareLinkRequestRef.current;
      return safeText(pendingToken);
    }

    shareLinkRequestRef.current = requestReferralShareToken({
      referralId: cleanReferralId,
      accessToken: cleanAccessToken,
    })
      .then(({ token, expiresAt }) => {
        shareLinkCacheRef.current = {
          referralId: cleanReferralId,
          token,
          expiresAt,
        };
        return token;
      })
      .finally(() => {
        shareLinkRequestRef.current = null;
      });

    return shareLinkRequestRef.current;
  }

  React.useEffect(() => {
    pdfPreparationRef.current = null;
    pdfPreparedRef.current = false;
    sessionAccessTokenRef.current = "";
    shareLinkCacheRef.current = { referralId: "", token: "", expiresAt: 0 };
    shareLinkRequestRef.current = null;
  }, [referralId]);

  const [consentRevision, setConsentRevision] = React.useState(0);
  React.useEffect(() => subscribeReferralConsent(() => setConsentRevision(value => value + 1)), []);

  React.useEffect(() => {
    let alive = true;

    async function load() {
      try {
        if (!referralId) {
          if (!alive) return;
          setReferral(null);
          setDocumentContext(null);
          setAcceptedRouting(null);
          setLoadState("not-found");
          return;
        }

        if (alive) {
          setLoadState("loading");
        }

        const { data: sessionData } = await supabase.auth.getSession();
        let session = sessionData?.session || null;
        if (!session) {
          session = await resolveValidatedSession({ signOutOnFailure: false });
        }

        if (!alive) return;
        if (!session?.access_token) {
          sessionAccessTokenRef.current = "";
          setReferral(null);
          setDocumentContext(null);
          setAcceptedRouting(null);
          setLoadState("error");
          return;
        }

        sessionAccessTokenRef.current = safeText(session.access_token);

        const { data, error } = await supabase
          .from("referrals")
          .select("*")
          .eq("id", referralId)
          .maybeSingle();

        if (error) {
          console.error("[ReferralDetails] load failed:", error);
          if (!alive) return;
          setReferral(null);
          setDocumentContext(null);
          setAcceptedRouting(null);
          setLoadState("error");
          return;
        }

        if (!alive) return;

        if (!data) {
          setReferral(null);
          setDocumentContext(null);
          setAcceptedRouting(null);
          setLoadState("not-found");
          return;
        }

        setReferral(data);

        const { data: packageRows, error: packageError } = await supabase
          .from("referral_packages")
          .select("*")
          .eq("referral_id", data.id)
          .neq("status", "superseded")
          .order("package_version", { ascending: false })
          .limit(1);
        const resolvedReferralPackage = packageError ? null : packageRows?.[0] || null;
        if (!packageError) setReferralPackage(resolvedReferralPackage);

        const [
          appointmentRes,
          doctorRes,
          patientProfileRes,
          patientAuthRes,
          acceptedRoutingRes,
          branding,
        ] = await Promise.all([
          data?.appointment_id
            ? supabase
                .from("appointments")
                .select("id, practice_id")
                .eq("id", data.appointment_id)
                .maybeSingle()
            : Promise.resolve({ data: null }),
          supabase
            .from("doctor_profiles")
            .select("user_id, full_name, first_name, last_name, register_number, medical_license_number")
            .eq("user_id", data.from_doctor_user_id)
            .maybeSingle(),
          supabase
            .from("patient_profiles")
            .select("user_id, full_name, first_name, last_name, salutation, date_of_birth")
            .eq("user_id", data.patient_user_id)
            .maybeSingle(),
          supabase
            .from("profiles")
            .select("user_id, display_name")
            .eq("user_id", data.patient_user_id)
            .maybeSingle(),
          supabase
            .from("referral_routing_requests")
            .select("id, referral_id, to_doctor_user_id, specialty_key, specialty_label, status, created_at")
            .eq("referral_id", data.id)
            .eq("status", "accepted")
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          loadDoctorBillingBranding(data.from_doctor_user_id).catch(() => ({})),
        ]);

        if (acceptedRoutingRes?.error) {
          console.error("[ReferralDetails] routing load failed:", acceptedRoutingRes.error);
        }

        const practiceId = appointmentRes?.data?.practice_id || null;

        const [consultancyRes, acceptedDoctorRes] = await Promise.all([
          practiceId
            ? supabase
                .from("doctor_consultancies")
                .select("id, facility_name, facility_address, facility_city, postal_code, facility_country")
                .eq("id", practiceId)
                .maybeSingle()
            : supabase
                .from("doctor_consultancies")
                .select("id, facility_name, facility_address, facility_city, postal_code, facility_country")
                .eq("doctor_id", data.from_doctor_user_id)
                .order("is_primary", { ascending: false })
                .order("updated_at", { ascending: false })
                .limit(1)
                .maybeSingle(),
          (resolvedReferralPackage?.receiving_doctor_user_id || acceptedRoutingRes?.data?.to_doctor_user_id)
            ? supabase
                .from("doctor_profiles")
                .select("user_id, full_name, first_name, last_name, salutation, primary_specialty, specialty")
                .eq("user_id", resolvedReferralPackage?.receiving_doctor_user_id || acceptedRoutingRes.data.to_doctor_user_id)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);

        if (!alive) return;

        const patientName = resolvePatientDisplayName({
          profile: patientProfileRes?.data,
          user: { display_name: patientAuthRes?.data?.display_name },
          fallback: buildDisplayName(patientProfileRes?.data, "") || safeText(patientAuthRes?.data?.display_name, "Patient"),
        });

        setAcceptedRouting(
          acceptedRoutingRes?.data
            ? {
                ...acceptedRoutingRes.data,
                doctorName: buildDoctorReferralName(acceptedDoctorRes?.data, ""),
              }
            : null
        );

        setDocumentContext({
          viewerRole: resolvedReferralPackage?.receiving_doctor_user_id === user?.id
            ? "receiving_clinician"
            : "patient",
          doctorName: buildDoctorReferralName(doctorRes?.data, "Doctor"),
          patientName,
          patientDob: patientProfileRes?.data?.date_of_birth || null,
          facilityName: safeText(consultancyRes?.data?.facility_name),
          facilityAddress: buildFacilityAddress(consultancyRes?.data),
          facilityAddressLines: buildFacilityPostalAddressLines(consultancyRes?.data),
          practiceLogoUrl: safeText(branding?.practiceLogoUrl),
          doctorSignatureUrl: safeText(branding?.doctorSignatureUrl),
          doctorRegistrationNumber: safeText(doctorRes?.data?.register_number || doctorRes?.data?.medical_license_number),
          acceptedDoctorName: buildDoctorReferralName(acceptedDoctorRes?.data, ""),
          acceptedDoctorSpecialty: safeText(
            acceptedDoctorRes?.data?.primary_specialty ||
              acceptedDoctorRes?.data?.specialty ||
              resolvedReferralPackage?.specialty_label,
          ),
          doctorStampBaseSrc: "/icons/appointments/e-prescription/afrimed-dr-stamp-base-flat.png",
        });

        setLoadState("ready");
      } catch (caughtError) {
        console.error("[ReferralDetails] hydrate failed:", caughtError);
        if (!alive) return;
        sessionAccessTokenRef.current = "";
        setReferral(null);
        setDocumentContext(null);
        setAcceptedRouting(null);
        setLoadState("error");
      }
    }

    if (authLoading) {
      return () => {
        alive = false;
      };
    }

    load();

    return () => {
      alive = false;
    };
  }, [referralId, authLoading, user?.id, consentRevision]);

  React.useEffect(() => {
    if (!referral?.id || !documentContext || !user?.id || user.id !== referral.patient_user_id) return undefined;
    if (pdfPreparedRef.current) return undefined;
    if (pdfPreparationRef.current) return undefined;

    const pdfCopy = buildReferralPdfCopy(t);
    pdfPreparationRef.current = ensureReferralPdfStored({
      referral,
      documentContext,
      patientUserId: referral.patient_user_id,
      copy: pdfCopy,
    })
      .then(() => {
        pdfPreparedRef.current = true;
      })
      .catch((error) => {
        pdfPreparedRef.current = false;
        console.error("[ReferralDetails] background referral PDF prep failed:", error);
      })
      .finally(() => {
        pdfPreparationRef.current = null;
      });

    return undefined;
  }, [referral, documentContext, user?.id, t]);

  async function handleOpenEmailDraft() {
    if (typeof window === "undefined" || !referral) return;

    setEmailDraftBusy(true);
    setEmailSendState({ error: "", success: "" });
    try {
      const response = await sendReferralUpdateEmail({
        referral_id: referral.id,
      });
      if (!response?.ok && !response?.skipped) {
        throw new Error(response?.error || "We could not send the referral email right now.");
      }
      if (response?.skipped) {
        throw new Error(
          t(
            "referrals.patient.emailSkipped",
            "This referral email is currently unavailable because communication preferences block it.",
          ),
        );
      }
      setEmailSendState({
        error: "",
        success: t(
          "referrals.patient.emailSent",
          "Referral email sent.",
        ),
      });
    } catch (error) {
      console.error("[ReferralDetails] referral email send failed:", error);
      setEmailSendState({
        error:
          String(error?.message || "").trim() ||
          t(
            "referrals.patient.emailSendError",
            "We could not send the referral email right now.",
          ),
        success: "",
      });
    } finally {
      setEmailDraftBusy(false);
    }
  }

  const packageDecisionLock = React.useRef(false);
  async function handlePackageDecision(decision, { createStandingAuthorisation = false } = {}) {
    if (!referralPackage || packageDecisionLock.current) return;
    packageDecisionLock.current = true;
    setPackageDecisionBusy(true);
    setPackageDecisionError("");
    try {
      await decideReferralPackage({
        referralPackage,
        decision,
        createStandingAuthorisation,
      });
      setReferralPackage((current) => current ? {
        ...current,
        status: decision,
        authorised_at: decision === "authorised" ? new Date().toISOString() : null,
      } : current);
      setAlwaysAllowConfirmOpen(false);
    } catch (error) {
      setPackageDecisionError(String(error?.message || "Unable to record your referral decision."));
    } finally {
      setPackageDecisionBusy(false);
      packageDecisionLock.current = false;
    }
  }

  const referringDoctorName =
    documentContext?.doctorName || t("referrals.patient.share.referringClinician", "your clinician");
  const receivingDoctorName =
    documentContext?.acceptedDoctorName ||
    t("referrals.patient.share.receivingSpecialist", "the receiving specialist");

  function handlePrint() {
    try {
      document.title = t("referrals.patient.title", "Referral");
    } catch {
      // The document remains printable when the browser prevents title changes.
    }
    window.print?.();
  }

  async function handleDownload() {
    if (!referral || !documentContext || downloadBusy) return;
    setDownloadBusy(true);
    try {
      const pdfBlob = await generateReferralPdfBlob({
        referral,
        documentContext,
        copy: buildReferralPdfCopy(t),
      });
      const objectUrl = window.URL.createObjectURL(pdfBlob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `referral-${referral.id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("[ReferralDetails] PDF download failed:", error);
      handlePrint();
    } finally {
      setDownloadBusy(false);
    }
  }

  return (
    <LayoutShell hideTabs flushTop freeScroll manageOwnTabLayout railClassName="w-full">
      <TopBarCenter
        title={t("referrals.patient.title", "Referral")}
        subtitle={t("referrals.patient.subtitle", "Your care pathway")}
        onBack={() => nav(-1)}
        right={
          <div className="no-print flex items-center gap-2">
            <ActionIconButton icon={ICON_PRINT} label={t("common.actions.print", "Print")} onClick={handlePrint} />
            <ActionIconButton icon={ICON_DOWNLOAD} label={t("common.actions.download", "Download")} onClick={handleDownload} disabled={downloadBusy} />
            {/* QR is intentionally deferred: it requires its own package-authorisation and revocation authority. */}
          </div>
        }
      />

      <div className="w-full min-w-0 max-w-full space-y-4 pt-4 pb-24">
          {loadState === "loading" ? (
            <div className="text-body-medium text-on-surface-var">
              {t("referrals.patient.loading", "Loading referral…")}
            </div>
          ) : loadState === "not-found" ? (
            <div className="text-body-medium text-on-surface-var">
              {t("referrals.patient.notFound", "Referral not found.")}
            </div>
          ) : loadState === "error" ? (
            <div className="text-body-medium text-on-surface-var">
              {t("referrals.patient.loadError", "Unable to load referral right now.")}
            </div>
          ) : (
            <>
              <ReferralSummaryCard referral={referral} documentContext={documentContext} printRef={printRef} />

              {documentContext?.viewerRole === "receiving_clinician" && referralPackage?.status === "authorised" ? (
                <section className="no-print px-1 pt-1">
                  <NotificationActionButton
                    loading={messageReferrerBusy}
                    disabled={messageReferrerBusy}
                    onClick={() => void messageReferringClinician()}
                  >
                    Message referring clinician
                  </NotificationActionButton>
                </section>
              ) : null}

              {referralPackage ? (
                <section className="no-print space-y-3 px-1 pt-1">
                  <div className="text-title-medium text-on-surface">
                    {t("referrals.patient.share.title", "Referral sharing")}
                  </div>
                  {["authorised", "delivered", "acknowledged"].includes(referralPackage.status) ? (
                    <div className="space-y-2">
                      <div className="text-label-large text-primary">
                        {t("referrals.patient.share.authorised", "Authorised")}
                      </div>
                      <div className="text-body-medium text-primary">
                        {t("referrals.patient.share.authorisedCopy", "{patientName} authorised this referral package to be shared with {doctorName}.", {
                          patientName: documentContext?.patientName || t("referrals.patient.share.patient", "The Patient"),
                          doctorName: receivingDoctorName,
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-body-medium text-on-surface-var">
                      {referralPackage.status === "ready_for_authorisation"
                        ? t("referrals.patient.share.prompt", "Your clinician has prepared this referral package. Review the referral above and decide whether the selected clinical information may be shared with the receiving specialist.")
                        : referralPackage.status === "declined" ? <div className="space-y-2"><Badge>{t("referrals.patient.share.declined", "Declined")}</Badge><p>{t("referrals.patient.share.declinedCopy", "This referral package has not been shared. No standing permission was created.")}</p></div> : <Badge>{t("referrals.patient.share.status", String(referralPackage.status || "").replaceAll("_", " "))}</Badge>}
                    </div>
                  )}
                  {referralPackage.status === "ready_for_authorisation" ? (
                    <div className="flex flex-wrap gap-3">
                      <NotificationActionButton
                        tone="danger"
                        className="flex-1"
                        loading={packageDecisionBusy}
                        disabled={packageDecisionBusy}
                        onClick={() => handlePackageDecision("declined")}
                      >
                        {t("referrals.patient.share.decline", "Decline")}
                      </NotificationActionButton>
                      <NotificationActionButton
                        className="flex-1"
                        loading={packageDecisionBusy}
                        disabled={packageDecisionBusy}
                        onClick={() => handlePackageDecision("authorised")}
                      >
                        {t("referrals.patient.share.allowOnce", "Allow once")}
                      </NotificationActionButton>
                      <NotificationActionButton
                        className="flex-1"
                        loading={packageDecisionBusy}
                        disabled={packageDecisionBusy}
                        onClick={() => setAlwaysAllowConfirmOpen(true)}
                      >
                        {t("referrals.patient.share.alwaysAllow", "Always allow")}
                      </NotificationActionButton>
                    </div>
                  ) : null}
                  {packageDecisionError ? <div className="text-body-small text-[rgb(var(--afrimed-error))]">{packageDecisionError}</div> : null}
                </section>
              ) : null}

              <BasicDialog
                open={alwaysAllowConfirmOpen}
                title={t(
                  "referrals.patient.share.alwaysAllowTitle",
                  "Always allow referrals from {referrerName} to {recipientName}",
                  { referrerName: referringDoctorName, recipientName: receivingDoctorName },
                )}
                body={t(
                  "referrals.patient.share.alwaysAllowBody",
                  "Future referral packages from {referrerName} to {recipientName} can be shared without asking you each time. You can change this later in Referral sharing.",
                  { referrerName: referringDoctorName, recipientName: receivingDoctorName },
                )}
                cancelText={t("common.cancel", "Cancel")}
                okText={t("referrals.patient.share.alwaysAllowConfirm", "Always allow")}
                disableOk={packageDecisionBusy}
                disableBackdropClose={packageDecisionBusy}
                onCancel={() => { if (!packageDecisionLock.current) setAlwaysAllowConfirmOpen(false); }}
                onOk={() => handlePackageDecision("authorised", { createStandingAuthorisation: true })}
              />

              {emailSendState.error ? (
                <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
                  {emailSendState.error}
                </div>
              ) : null}

              {emailSendState.success ? (
                <div className="rounded-[12px] bg-[var(--state-positive-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-positive))]">
                  {emailSendState.success}
                </div>
              ) : null}
            </>
          )}
      </div>
    </LayoutShell>
  );
}
